import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Search } from 'lucide-react';

const nameOf = (u) => u.name || u.full_name || u.display_name || u.email || u.user_id;
const initials = (n) => (n || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

// Pick WHO to share the current view with: a specific teammate (delivered as a
// direct message) or everyone on this object's chat.
export default function ShareViewDialog({ open, onOpenChange, onShare }) {
  const { user } = useAuth();
  const me = user?.user_id;
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setQ('');
    api.getPresence()
      .then(r => setUsers((r?.users || []).filter(u => (u.user_id || u.id) !== me)))
      .catch(() => setUsers([]));
  }, [open, me]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter(u => nameOf(u).toLowerCase().includes(s));
  }, [users, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-view-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Share this view with…</DialogTitle>
        </DialogHeader>
        <button
          onClick={() => onShare(null)}
          className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-border/40 hover:border-primary/40 hover:bg-primary/5 text-left"
          data-testid="share-view-everyone">
          <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Users className="w-4 h-4 text-muted-foreground" /></span>
          <span>
            <span className="block text-sm text-foreground">Everyone on this report</span>
            <span className="block text-[11px] text-muted-foreground">Post to the report's chat</span>
          </span>
        </button>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search teammates" className="pl-8" data-testid="share-view-search" />
        </div>
        <div className="max-h-64 overflow-auto space-y-1">
          {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No teammates found.</p>}
          {filtered.map(u => {
            const uid = u.user_id || u.id;
            const n = nameOf(u);
            return (
              <button key={uid} onClick={() => onShare({ user_id: uid, name: n })}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 text-left"
                data-testid={`share-view-user-${uid}`}>
                <span className="w-8 h-8 rounded-full bg-indigo-500 text-white text-[11px] font-semibold flex items-center justify-center">{initials(n)}</span>
                <span className="text-sm text-foreground truncate">{n}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
